import { motion } from "framer-motion";
import { Crosshair, Dumbbell, Feather, Code2, LineChart, Instagram, Book, Trophy } from "lucide-react";
import { BOOKS, LITERARY_AWARDS, PROFILE } from "../data/cv";
import { fadeUp, stagger, viewportOnce, onSpotMove } from "../lib/anim";

// Order matters: professional-adjacent pursuits first, poetry & authorship LAST.
const PURSUITS = [
  {
    icon: Code2,
    name: "Coding",
    edge: "Hands-on development behind four production health-tech products — from specification to prototype to shipping.",
  },
  {
    icon: LineChart,
    name: "Finance & investing",
    edge: "Market analysis and capital allocation — a practical complement to an MBA in healthcare management.",
  },
  {
    icon: Crosshair,
    name: "Air pistol & rifle shooting",
    edge: "Precision sport built on breath control and fine-motor stability — directly useful at the procedure table.",
  },
  {
    icon: Dumbbell,
    name: "Boxing",
    edge: "Structured training, conditioning and composure under pressure.",
  },
  {
    icon: Feather,
    name: "Poetry & authorship",
    edge: "Published author of three books and a contributor to five anthologies.",
    literary: true,
  },
];

export default function Passion() {
  return (
    <section id="passion" className="relative py-16 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
          <motion.p variants={fadeUp} className="font-mono text-xs tracking-[0.22em] uppercase text-neon mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-gradient-to-r from-neon to-transparent" /> 06 — Passion &amp; pursuits
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Beyond the lab: <span className="text-grad">discipline that carries into the work.</span>
          </motion.h2>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* Motto */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="lg:col-span-5 panel spot-card rounded-2xl p-6 sm:p-8 flex flex-col"
            data-testid="motto-card"
          >
            <span className="font-display text-6xl leading-none text-grad select-none">"</span>
            <blockquote className="mt-2 font-display text-xl sm:text-2xl font-semibold leading-snug tracking-tight text-slate-100">
              Plan to win, <span className="text-grad">prepare to win, and expect to win.</span>
            </blockquote>
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              Working principle
            </p>
            <p className="mt-8 text-sm text-slate-400 leading-relaxed border-l border-vio/30 pl-4">
              Preparation, consistency and measurable outcomes — the same standard applied to lab quality, product
              delivery and every client engagement.
            </p>
            <a
              href={PROFILE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="passion-instagram-link"
              className="mt-auto pt-8 inline-flex items-center gap-2 text-sm font-mono text-slate-400 hover:text-fuchsia-300 transition-colors duration-300"
            >
              <Instagram size={15} /> More on {PROFILE.instagramHandle}
            </a>
          </motion.div>

          {/* Pursuits */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {PURSUITS.map((p, i) => (
              <motion.div
                key={p.name}
                variants={fadeUp}
                custom={i + 1}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                onMouseMove={onSpotMove}
                className={`panel spot-card rounded-2xl p-6 ${i >= PURSUITS.length - 1 ? "sm:col-span-2" : ""}`}
                data-testid="pursuit-card"
              >
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-xl border border-white/10 bg-white/[0.04] text-neon">
                    <p.icon size={17} />
                  </span>
                  <h3 className="font-display text-base font-semibold tracking-tight">{p.name}</h3>
                </div>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">{p.edge}</p>
                {p.literary && (
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="literary-card">
                    <div className="space-y-3">
                      <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-500">Books</p>
                      {BOOKS.map((b) => (
                        <div key={b.title} className="flex items-start gap-3" data-testid="book-row">
                          <Book size={14} className="text-neon mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm text-slate-200">{b.title}</p>
                            <p className="text-[11px] font-mono text-slate-500">{b.year}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2.5">
                      <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-500 flex items-center gap-2">
                        <Trophy size={12} className="text-vio" /> Literary awards
                      </p>
                      {LITERARY_AWARDS.map((a) => (
                        <p key={a} className="text-xs text-slate-400 leading-relaxed border-l border-vio/30 pl-3">{a}</p>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
